<template>
    <cs-app>
        <template v-slot:content>
            <div class="heading">
                <router-link :to="{name: 'lawyer.cases.index'}" style="align-self: flex-start;">
                    <md-button class="default">
                        <md-icon>chevron_left</md-icon>Back
                    </md-button>
                </router-link>
                <md-button
                    v-if="!bidPlacementNotAllowed"
                    @click="openPlaceBidDialog"
                    class="md-primary"
                >
                    Place Bid
                </md-button>
                <p v-else>Looks like you've already bid on this case. You can only bid once per case.</p>
            </div>
            <h2 class="md-title">{{ clientCase.form.title }}</h2>
            <case-details :claimId="$route.params.id" />

            <!-- Place Bid Dialog -->
            <md-dialog id="place-bid-dialog" :md-active.sync="showPlaceBidDialog" v-if="showPlaceBidDialog">
                <md-dialog-title>Place a Bid</md-dialog-title>
                <md-dialog-content>
                    <!-- No bids state -->
                    <div class="no-bids-content" v-if="!hasBids">
                        Looks like you don't have any bids to place on this case. You'll have to purchase some
                        bids first before you can place them.
                    </div>

                    <!-- Has bids state -->
                    <div class="has-bids-content" v-if="hasBids">
                        <p>Set your contingencies for this bid.</p>
                        <div>
                            <md-field :class="getValidationClass('preDiscovery')">
                                <label for="pre-discovery">Pre-Discovery Percentage</label>
                                <md-input
                                    name="pre-discovery"
                                    v-model="form.preDiscovery"
                                    placeholder="20.5"></md-input>%
                                <span class="md-error" v-if="!$v.form.preDiscovery.required">
                                    Pre-Discovery is required
                                </span>
                                <span class="md-error" v-else-if="!$v.form.preDiscovery.maxLength">
                                    Pre-Discovery can't be more than 3 digits long
                                </span>
                                <span class="md-error" v-else-if="!$v.form.preDiscovery.maxValue">
                                    Pre-Discovery can't be more than 100%
                                </span>
                            </md-field>
                            <md-field :class="getValidationClass('preTrial')">
                                <label for="pre-trial">Pre-Trial Percentage</label>
                                <md-input name="pre-trial" v-model="form.preTrial" placeholder="20.5"></md-input>%
                                <span class="md-error" v-if="!$v.form.preTrial.required">
                                    Pre-Trial is required
                                </span>
                                <span class="md-error" v-else-if="!$v.form.preTrial.maxLength">
                                    Pre-Trial can't be more than 3 digits long
                                </span>
                                <span class="md-error" v-else-if="!$v.form.preTrial.maxValue">
                                    Pre-trial can't be more than 100%
                                </span>
                            </md-field>
                            <md-field :class="getValidationClass('preCourtResolution')">
                                <label for="pre-court">Pre-Court Resolution Percentage</label>
                                <md-input
                                    name="pre-court"
                                    v-model="form.preCourtResolution"
                                    placeholder="20.5"></md-input>%
                                <span class="md-error" v-if="!$v.form.preCourtResolution.required">
                                    Pre-Court Resolution is required
                                </span>
                                <span class="md-error" v-else-if="!$v.form.preCourtResolution.maxLength">
                                    Pre-Court can't be more than 3 digits long
                                </span>
                                <span class="md-error" v-else-if="!$v.form.preCourtResolution.maxValue">
                                    Pre-Court can't be more than 100%
                                </span>
                            </md-field>
                        </div>
                    </div>
                </md-dialog-content>
                <md-dialog-actions>
                    <md-button class="md-default" @click="dialogsOff">Close</md-button>
                    <md-button class="md-primary" v-if="hasBids" @click="validateBid">Place Bid</md-button>
                    <md-button class="md-primary" v-if="!hasBids" @click="openPurchaseBid">Purchase A Bid</md-button>
                </md-dialog-actions>
            </md-dialog>

            <!-- Place Bid Step 2 Dialog -->
            <md-dialog
                id="place-bid-step-2-dialog"
                :md-active.sync="showPlaceBidStep2Dialog"
                v-if="showPlaceBidStep2Dialog"
            >
                <md-dialog-title>Place Bid</md-dialog-title>
                <md-dialog-content>
                    <p>Are you sure you would like to place a bid on this case? This is a non-refundable action.</p>
                    <strong>Contingentcy Summary</strong>
                    <ul>
                        <li>Pre-Discovery: {{ form.preDiscovery ? form.preDiscovery : 'N/A' }}%</li>
                        <li>Pre-Trial: {{ form.preTrial ? form.preTrial : 'N/A' }}%</li>
                        <li>Pre-Court Resolution: {{ form.preCourtResolution ? form.preCourtResolution : 'N/A' }}%</li>
                    </ul>
                    <span v-if="bidPlacementNotAllowed" style="color: red;">You've already placed a bid on this case.</span>
                </md-dialog-content>
                <md-dialog-actions>
                    <md-button class="md-default" @click="dialogsOff">Close</md-button>
                    <md-button class="md-primary" @click="placeBid" :disabled="bidPlacementNotAllowed">Yes</md-button>
                </md-dialog-actions>
            </md-dialog>

            <!-- Place Bid Success Dialog -->
            <md-dialog
                id="place-bid-success-dialog"
                :md-active.sync="showPlaceBidSuccessDialog"
                v-if="showPlaceBidSuccessDialog"
            >
                <md-dialog-title>Success!</md-dialog-title>
                <md-dialog-content>
                    <p>Your bid has been placed successfully!</p>
                </md-dialog-content>
                <md-dialog-actions>
                    <md-button class="md-default" @click="dialogsOff">Close</md-button>
                </md-dialog-actions>
            </md-dialog>

            <!-- Purchase Bid Dialog -->
            <md-dialog id="purchase-bid-dialog" :md-active.sync="showPurchaseBidDialog" v-if="showPurchaseBidDialog">
                <md-dialog-title>Purcahase Bid</md-dialog-title>
                <md-dialog-content>
                    <p>Are you sure you'd like to purchase a bid?</p>
                    <p>
                        Bids are $50 each. After purchasing one you'll be able to place it on a case. You
                        can purchase as many bids as you'd like, but curretnly we only allow one to be purchased at a
                        time.
                    </p>
                    <p>This purchase will be automatically billed to your account.</p>
                    <strong>Total: $50</strong>
                </md-dialog-content>
                <md-dialog-actions>
                    <md-button class="md-default" @click="dialogsOff">Close</md-button>
                    <md-button class="md-primary" @click="purchaseBid">Buy</md-button>
                </md-dialog-actions>
            </md-dialog>

            <!-- Purchase Bid Confirmation Dialog -->
            <md-dialog
                id="purchase-confirmation-dialog"
                :md-active.sync="showPurchaseConfirmationDialog"
                v-if="showPurchaseConfirmationDialog"
            >
                <md-dialog-title>Success!</md-dialog-title>
                <md-dialog-content>
                    <p>
                        Thank you for purchasing a bid! Please save your transaction id shown below in a safe place.
                    </p>
                    <strong>Transaction ID: {{ chargeId }}</strong>
                    <p>
                        Now you can place this bid on the current case your viewing. Or place it on another case.
                    </p>
                    <md-dialog-actions>
                        <md-button class="md-default" @click="dialogsOff">Close</md-button>
                        <md-button class="md-primary"
                            v-if="hasBids"
                            @click="openPlaceBidDialog"
                        >
                            Place a Bid
                        </md-button>
                    </md-dialog-actions>
                </md-dialog-content>
            </md-dialog>


        </template>
    </cs-app>
</template>

<script>
import CSApp from '@/components/CSApp'
import CaseDetails from '@/components/CaseDetails'
import { caseService } from '@/services/caseService'
import { mapGetters } from 'vuex'
import { setTimeout } from 'timers'
import { validationMixin } from 'vuelidate'
import {
  required,
  email,
  minLength,
  maxLength
} from 'vuelidate/lib/validators'
import _ from 'lodash'

export default {
    name: 'LawyerCaseDetails',
    props: ['caseType'],
    mixins: [validationMixin],
    data: () => ({
        bidPlacementNotAllowed: false,
        contingentsEnum: false,
        showPlaceBidDialog: false,
        showPlaceBidStep2Dialog: false,
        showPlaceBidSuccessDialog: false,
        showPurchaseBidDialog: false,
        showPurchaseConfirmationDialog: false,
        chargeId: null,
        form: {
            preDiscovery: null,
            preTrial: null,
            preCourtResolution: null,
        }
    }),
    validations: {
        form: {
            preDiscovery: {
                required,
                // max length is 4 to accomadate the decimal
                maxLength: maxLength(4),
                maxValue: value => value <= 100,
            },
            preTrial: {
                required,
                maxLength: maxLength(4),
                maxValue: value => value <= 100,
            },
            preCourtResolution: {
                required,
                maxLength: maxLength(4),
                maxValue: value => value <= 100,
            }
        }
    },
    components: {
        'cs-app': CSApp,
        CaseDetails,
    },
    computed: {
        hasBids () {
            return this.purchasedBids > 0
        },
        ...mapGetters([
            'clientCase',
            'purchasedBids'
        ])
    },
    created () {
        this.$http.get(`${process.env.VUE_APP_API_URL}/bids`).then(response => {
            if (!response.data.length > 0) {
                // The lawyer has no bids so bid placement is defintely allowed
                return;
            };

            const bid = response.data.find(bid => bid.caseId === parseInt(this.$route.params.id))

            if (bid) {
                // We found a bid with the current case id so bid placement is not allowed
                this.bidPlacementNotAllowed = true;
            }
        }).catch(error => console.log(error));

        caseService(this.$http).whereId(this.$route.params.id).get().then(response => {
            this.$store.commit('addClientCase', response.data)
            this.fields = caseService(this.$http).mapCaseFieldsToAnswers(this.clientCase)
        }).catch(error => {
            console.log(error)
        })

        this.$http.get(`${process.env.VUE_APP_API_URL}/contingents`).then(response => {
            this.contingentsEnum = response.data
        }).catch(error => {
            console.log(error)
        })
    },
    methods: {
        getValidationClass(fieldName) {

            const field = _.get(this.$v.form, fieldName);

            if (field) {
                return {
                    "md-invalid": field.$invalid && field.$dirty
                };
            }
        },
        validateBid () {
            this.$v.$touch()

            if(!this.$v.$invalid) {
                this.openPlaceBid()
            }
        },
        openPurchaseBid () {
            this.dialogsOff()
            this.showPlaceBidDialog = false
            setTimeout(() => {this.showPurchaseBidDialog = true}, 500)

        },
        purchaseBid () {
            this.$http.post(`${process.env.VUE_APP_API_URL}/bids`).then(response => {
                this.dialogsOff()
                this.showPurchaseBidDialog = false
                this.chargeId = response.data.stripeChargeId
                this.$store.commit('incrementPurchasedBids', 1)
                setTimeout(() => {this.showPurchaseConfirmationDialog = true}, 500)
            }).catch(error => {
                console.log(error)
            })
        },
        openPlaceBid () {
            this.dialogsOff()
            setTimeout(() => {this.showPlaceBidStep2Dialog = true}, 500)
        },
        placeBid () {
            let payload = this.contingentsEnum.map(contingent => {
                let percentage = this.form[_.camelCase(contingent.name)]
                return {
                    percentage: parseFloat(percentage).toFixed(1),
                    contingentId: contingent.id
                }
            })
            this.$http.post(`${process.env.VUE_APP_API_URL}/cases/${this.$route.params.id}/bids`, {
                bidContingents: payload
            }).then(response => {
                this.$store.commit('decrementPurchasedBids', 1)
                this.dialogsOff();
                setTimeout(() => {this.showPlaceBidSuccessDialog = true}, 500)
            }).catch(error => {
                console.log(error)
                console.log(error.response)
                if (error.response.status === 403) {
                    this.bidPlacementNotAllowed = true
                }
            })
        },
        dialogsOff () {
            this.showPlaceBidDialog = false
            this.showPurchaseBidDialog = false
            this.showPurchaseConfirmationDialog = false
            this.showPlaceBidStep2Dialog = false
            this.showPlaceBidSuccessDialog = false
        },
        openPlaceBidDialog () {
            this.dialogsOff()
            setTimeout(() => {this.showPlaceBidDialog = true}, 500)
        }
    }
}
</script>

<style lang="scss" scoped>
.heading {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    .md-button {
        width: 100px;
    }
}
</style>